import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { type GetBrokersRequiredPaymentsAutopayStatesItem } from 'utils/web_api.schemas'

import { useRequiredPaymentsContext } from './RequiredPaymentsContext'
import TextCollection from './TextCollection'

const MONTH_LABELS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

const QueryBuilder = () => {
  const currentYear = new Date().getFullYear()
  const {
    autopayStates: autopayStateOptions,
    queryParams,
    setQueryParams,
  } = useRequiredPaymentsContext()

  const handleAutopayStateToggle = (state: GetBrokersRequiredPaymentsAutopayStatesItem) => {
    const updatedAutopayStates = queryParams.autopayStates.includes(state)
      ? queryParams.autopayStates.filter((s) => s !== state)
      : [...queryParams.autopayStates, state]
    setQueryParams({ ...queryParams, autopayStates: updatedAutopayStates })
  }

  const handleForMonthChange = (value: string) => {
    setQueryParams({ ...queryParams, forMonth: parseInt(value, 10) })
  }

  const handleForYearChange = (value: string) => {
    setQueryParams({ ...queryParams, forYear: parseInt(value, 10) })
  }

  const handleAddExcludedCarrierName = (carrierName: string) => {
    const excludedCarrierNames = Array.from(
      new Set([...queryParams.excludedCarrierNames, carrierName.trim()])
    )
    setQueryParams({ ...queryParams, excludedCarrierNames })
  }

  const handleRemoveExcludedCarrierName = (carrierName: string) => {
    const excludedCarrierNames = queryParams.excludedCarrierNames.filter((n) => n !== carrierName)
    setQueryParams({ ...queryParams, excludedCarrierNames })
  }

  const handleAddIncludedCarrierName = (carrierName: string) => {
    const includedCarrierNames = Array.from(
      new Set([...queryParams.includedCarrierNames, carrierName.trim()])
    )
    setQueryParams({ ...queryParams, includedCarrierNames })
  }

  const handleRemoveIncludedCarrierName = (carrierName: string) => {
    const includedCarrierNames = queryParams.includedCarrierNames.filter((n) => n !== carrierName)
    setQueryParams({ ...queryParams, includedCarrierNames })
  }

  return (
    <div className='space-y-4'>

      {/* Month and Year */}
      <div className='grid grid-cols-2 gap-4'>
        <div className='space-y-2'>
          <Label htmlFor='month'>Month</Label>
          <Select
            onValueChange={handleForMonthChange}
            value={queryParams.forMonth.toString()}
          >
            <SelectTrigger id='month'>
              <SelectValue placeholder='Select month' />
            </SelectTrigger>
            <SelectContent>
              {MONTH_LABELS.map(({ label, value }) => (
                <SelectItem key={value} value={value.toString()}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-2'>
          <Label htmlFor='year'>Year</Label>
          <Select
            onValueChange={handleForYearChange}
            value={queryParams.forYear.toString()}
          >
            <SelectTrigger id='year'>
              <SelectValue placeholder='Select year' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={currentYear.toString()}>
                {currentYear}
              </SelectItem>
              <SelectItem value={(currentYear + 1).toString()}>
                {currentYear + 1}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Autopay States */}
      <div className='space-y-2'>
        <Label>Autopay States</Label>
        <div className='flex flex-wrap gap-2'>
          {autopayStateOptions.map((state: GetBrokersRequiredPaymentsAutopayStatesItem) => (
            <label key={state} className='flex items-center space-x-2 cursor-pointer'>
              <input
                className='rounded border-gray-300'
                type='checkbox'
                checked={queryParams.autopayStates.includes(state)}
                onChange={() => handleAutopayStateToggle(state)}
              />
              <span className='text-sm'>{state}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Excluded Carriers */}
      <div className='space-y-2'>
        <Label htmlFor='excluded-carriers'>Excluded Carrier Names</Label>
        <TextCollection
          placeholder='e.g. Oscar; Aetna, Inc.; etc.'
          onAddItem={handleAddExcludedCarrierName}
          onRemoveItem={handleRemoveExcludedCarrierName}
          value={queryParams.excludedCarrierNames}
        />
      </div>

      {/* Included Carriers */}
      <div className='space-y-2'>
        <Label htmlFor='included-carriers'>Included Carrier Names (clear to include all carriers)</Label>
        <TextCollection
          placeholder='e.g. Blue Cross, LLC; United Healthcare'
          onAddItem={handleAddIncludedCarrierName}
          onRemoveItem={handleRemoveIncludedCarrierName}
          value={queryParams.includedCarrierNames}
        />
      </div>

    </div>
  )
}

export default QueryBuilder
